import { createContext, useContext, useEffect, useState, useCallback } from 'react';

/**
 * Central hub for PWA-install state. Everything the UI needs to know about
 * installability lives here: whether the browser supports it, whether it's
 * already installed, whether we have a deferred prompt event to fire.
 *
 * Why a Context instead of separate hooks in every component:
 *   The `beforeinstallprompt` event is fired ONCE per page load. Multiple
 *   `useEffect` listeners racing to capture it would be brittle. One provider
 *   captures once, distributes to consumers (banner + footer link).
 *
 * Detection strategy:
 *   - We consider a browser "capable" only if it fires beforeinstallprompt.
 *     That excludes iOS Safari, iOS Chrome (WebKit), and Firefox mobile —
 *     matching the user's 1b decision.
 *   - `matchMedia('(display-mode: standalone)')` and iOS `navigator.standalone`
 *     tell us whether we're already running as an installed app.
 *
 * Cooldown:
 *   `casaothers.installBannerDismissedAt` — timestamp of last dismissal.
 *   `INSTALL_COOLDOWN_MS` (7 days) blocks re-showing the top banner. The
 *   footer link is NOT gated by this cooldown so users still have a path
 *   to install after clicking "×".
 */
const InstallContext = createContext(null);

const COOLDOWN_KEY = 'casaothers.installBannerDismissedAt';
const INSTALLED_KEY = 'casaothers.installed'; // extra safety flag we set on appinstalled
const INSTALL_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;

const isStandalone = () => {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)')?.matches ||
    // iOS Safari legacy — kept because navigator.standalone is still true on
    // an already-installed iOS PWA even though we don't PROMPT on iOS.
    window.navigator?.standalone === true
  );
};

export const InstallProvider = ({ children }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(() => {
    if (isStandalone()) return true;
    try { return localStorage.getItem(INSTALLED_KEY) === '1'; } catch { return false; }
  });

  useEffect(() => {
    // Fires on Chrome/Edge/Samsung Internet when Google's install heuristics
    // agree we're installable AND the user hasn't dismissed too recently.
    const onBeforePrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    // Fires after Chrome finishes an install (via our banner OR the URL-bar
    // icon). We clear all install UI immediately.
    const onInstalled = () => {
      setDeferredPrompt(null);
      setInstalled(true);
      try { localStorage.setItem(INSTALLED_KEY, '1'); } catch { /* private mode */ }
    };
    window.addEventListener('beforeinstallprompt', onBeforePrompt);
    window.addEventListener('appinstalled', onInstalled);
    // Some browsers keep serving standalone matchMedia change events —
    // hook that too in case the user installs mid-session.
    const mm = window.matchMedia?.('(display-mode: standalone)');
    const onModeChange = (ev) => { if (ev.matches) onInstalled(); };
    mm?.addEventListener?.('change', onModeChange);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforePrompt);
      window.removeEventListener('appinstalled', onInstalled);
      mm?.removeEventListener?.('change', onModeChange);
    };
  }, []);

  /**
   * Fire the actual browser install dialog. Must be called synchronously
   * from a user gesture (click) or Chrome silently drops it.
   */
  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return { outcome: 'unavailable' };
    // Chrome invalidates the event after prompt() — even on dismiss — so we
    // drop our reference regardless of outcome.
    const evt = deferredPrompt;
    setDeferredPrompt(null);
    try {
      await evt.prompt();
      const choice = await evt.userChoice;
      if (choice.outcome === 'accepted') {
        // `appinstalled` will fire and setInstalled(true) via listener above.
        return { outcome: 'accepted' };
      }
      return { outcome: 'dismissed' };
    } catch {
      return { outcome: 'error' };
    }
  }, [deferredPrompt]);

  const canInstall = !installed && !!deferredPrompt;

  /**
   * Whether the auto-appearing top banner should be shown right now.
   * Distinct from `canInstall` because we honour a 7-day cooldown for the
   * banner but NOT for the footer link — the footer stays accessible so a
   * dismisser can still install later.
   */
  const shouldShowBanner = (() => {
    if (!canInstall) return false;
    try {
      const raw = localStorage.getItem(COOLDOWN_KEY);
      if (!raw) return true;
      const ts = parseInt(raw, 10);
      if (Number.isNaN(ts)) return true;
      return Date.now() - ts > INSTALL_COOLDOWN_MS;
    } catch {
      return true;
    }
  })();

  const dismissBanner = () => {
    try { localStorage.setItem(COOLDOWN_KEY, String(Date.now())); } catch { /* noop */ }
  };

  return (
    <InstallContext.Provider
      value={{ canInstall, installed, shouldShowBanner, promptInstall, dismissBanner }}
    >
      {children}
    </InstallContext.Provider>
  );
};

export const useInstall = () => {
  const ctx = useContext(InstallContext);
  if (!ctx) {
    // Return safe defaults so consumers outside a provider render nothing
    // rather than crashing (helpful during tests / storybook).
    return {
      canInstall: false, installed: false, shouldShowBanner: false,
      promptInstall: async () => ({ outcome: 'unavailable' }),
      dismissBanner: () => {},
    };
  }
  return ctx;
};
